import { memo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useAudio, useAuth, useConfig } from '@chainlit/react-client';
import { activeAudioButtonState } from '@/state/chat';

import AudioPresence from '@/components/AudioPresence';
import RealtimeAudioVisualizer from '@/components/RealtimeAudioVisualizer';
import AIAssistantOverlay from '@/components/AIAssistantOverlay';
import FireUpAnimation from '@/components/FireUpAnimation';
import AIAvatarButton from './AIAvatarButton';
import ButtonLink from '@/components/ButtonLink';
import { useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useUISettings } from '@/hooks/useUISettings';

import ApiKeys from './ApiKeys';
import ChatProfiles from './ChatProfiles';
import NewChatButton from './NewChat';
import ReadmeButton from './Readme';
import SidebarTrigger from './SidebarTrigger';
import { ThemeToggle } from './ThemeToggle';
import UserNav from './UserNav';

const Header = memo(() => {
  const { audioConnection } = useAudio();
  const activeButton = useRecoilValue(activeAudioButtonState);
  const navigate = useNavigate();
  const { data } = useAuth();
  const { config } = useConfig();
  const { open, openMobile, isMobile } = useSidebar();
  const { settings } = useUISettings();
  const [showOverlay, setShowOverlay] = useState(false);
  const [showFireUp, setShowFireUp] = useState(false);
  const prevActiveRef = useRef(false);

  const sidebarOpen = isMobile ? openMobile : open;

  const historyEnabled = data?.requireLogin && config?.dataPersistence;

  const links = config?.ui?.header_links || [];
  
  // Check if assistant mode is active
  const isAssistantActive = audioConnection === 'on' && activeButton === 'assistant';
  const isStandardActive = audioConnection === 'on' && activeButton === 'standard';
  
  // Get colors and titles from settings with fallback to defaults
  const gradientStart = settings?.colors?.headerGradientStart || '#152644';
  const gradientEnd = settings?.colors?.headerGradientEnd || '#5B9BD5';
  const headerTitle = settings?.titles?.headerTitle || 'Aviation Virtual Assistant';
  
  // Show overlay and fire up animation when assistant first activates
  // After that, overlay can be manually toggled without affecting the session
  useEffect(() => {
    if (isAssistantActive && !prevActiveRef.current) {
      // Just activated - show fire up animation and overlay
      setShowFireUp(true);
      setShowOverlay(true);
    } else if (!isAssistantActive) {
      // Session ended - close overlay and reset
      setShowOverlay(false);
      setShowFireUp(false);
    }
    // Note: If session is active and user closes overlay, we don't auto-reopen it
    prevActiveRef.current = isAssistantActive;
  }, [isAssistantActive]);
  return (
    <div
      className={cn(
        'p-3 flex h-[60px] items-center justify-between gap-2 relative',
        // Mobile: reduced padding
        'px-2 sm:px-3'
      )}
      id="header"
    >
      <div className="flex items-center gap-4">
        {/* Left side: Logo and Title */}
        <img 
          src="https://stazureaioai675868417442.blob.core.windows.net/signatureaviation/sa_logo_blue.jpg" 
          alt="Signature Aviation" 
          className="h-6 w-auto sm:h-8 flex-shrink-0"
          onError={(e) => {
            console.error('Failed to load logo from URL');
            e.currentTarget.style.display = 'none';
          }}
        />
        <div className="flex flex-col min-w-0 flex-1 justify-center">
          {/* Title */}
          <div className="flex items-baseline gap-1 sm:gap-2">
           <span 
              className="font-medium text-xs sm:text-base lg:text-lg break-words"
              style={{
                background: `linear-gradient(to right, ${gradientStart}, ${gradientEnd})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
             {headerTitle}
            </span>
          </div>
          {/* Audio Presence Indicator - Different for standard vs assistant */}
          {audioConnection === 'on' && (
            <div className="flex items-center mt-1 gap-2">
              {isAssistantActive ? (
                <>
                  <RealtimeAudioVisualizer size={32} isActive={true} />
                  <span className="text-xs font-medium bg-gradient-to-r from-cyan-500 to-violet-500 bg-clip-text text-transparent animate-pulse">
                    AI Listening
                  </span>
                </>
              ) : isStandardActive ? (
                <AudioPresence
                  type="server"
                  height={35}
                  width={70}
                  barCount={4}
                  barSpacing={2}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* Right side: Controls */}
      <div className="flex items-center gap-2">
        <div className="hidden sm:flex items-center gap-2">
          <ChatProfiles navigate={navigate} />
          <ApiKeys />
          <AIAvatarButton onOverlayToggle={() => setShowOverlay(true)} />
          <ThemeToggle />
          <UserNav />
        </div>
        <div className="sm:hidden">
          <AIAvatarButton onOverlayToggle={() => setShowOverlay(true)} />
          <SidebarTrigger />
        </div>
      </div>

      {/* Full-screen AI Assistant Overlay */}
      {showOverlay && (
        <AIAssistantOverlay onClose={() => setShowOverlay(false)} />
      )}
      
      {/* Fire Up Animation */}
      <FireUpAnimation 
        isActive={showFireUp} 
        onComplete={() => setShowFireUp(false)} 
      />
    </div>
  );
});

export { Header };
